import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FaArrowLeft, FaCcMastercard, FaCcVisa, FaCheckCircle, FaClipboardList, FaCreditCard, FaDumbbell, FaEdit, FaExclamationTriangle, FaIdCard, FaSwimmingPool, FaTimesCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Cropper from 'react-easy-crop';
import { getCardApplicationFee, getMembershipPlans, initiateApplicationPayment } from '../../../api/cardService';
import { initiateGeneralPayment } from '../../../api/paymentService';
import './CardApplicationForm.scss';

const initialFormData = {
  programType: '',
  firstName: '',
  lastName: '',
  identityNumber: '',
  email: '',
  phone: '',
  phone2: '',
  userGender: '',
  profession: '',
  homeAddress: '',
  membershipPlanId: '',
  subscriptionDuration: '',
  birthDate: '',
  photoBase64: '',
  termsAccepted: false
};

const initialPaymentData = {
  cardNumber: '',
  cardName: '',
  expiryMonth: '',
  expiryYear: '',
  cvv: ''
};

const PROGRAM_OPTIONS = {
  Pool: {
    title: 'Havuz',
    description: 'Havuz kartı ve üyelik başvurusu',
    detail: 'Yüzme havuzu kart başvurusu ve seans paketi seçimi',
    icon: FaSwimmingPool
  },
  Fitness: {
    title: 'Fitness',
    description: 'Fitness kartı ve kontörlü üyelik başvurusu',
    detail: 'Fitness randevuları için kart başvurusu ve paket seçimi',
    icon: FaDumbbell
  }
};

const FITNESS_HEALTH_DECLARATION_TITLE = 'T.C. \u0130SKENDERUN BELED\u0130YES\u0130 FITNESS CENTER';
const FITNESS_HEALTH_DECLARATION_PARAGRAPHS = [
  'Spor yapmama ve Fitness Salonunu kullanmama engel bir hastalığım olmadığını, sağlık durumumun iyi olduğunu, aktif egzersizlere katılmama engel bir özrümün, sakatlığımın, rahatsızlığımın veya yasağın bulunmadığını, bu tip egzersizlere katılmamın sağlığıma ve fiziksel durumuma bir zarar vermeyeceğini, her türlü egzersizlerin sonucunda doğabilecek yaralanma ve zararların sonucuna katlanacağımı, oluşabilecek her türlü sağlık problemlerinde ve diğer olumsuzluklarda sorumluluğun tarafıma ait olduğunu beyan ederim. İSKENDERUN BELEDİYESİ FITNESS CENTER bu beyanın yanlış, eksik veya yanıltıcı olarak verilmiş olmasından doğabilecek hasarlardan ve sakatlanmalardan sorumlu değildir.',
  'Salon içerisindeki aletleri kullanırken ve yapılan çalışma programını uygularken oluşabilecek yaralanmalardan ve incinmelerden doğan hasar ve zarara ilişkin iddia, talep, hukuk ve ceza davalarında İSKENDERUN BELEDİYESİ FITNESS CENTER ve çalışanlarını sorumlu tutmayacağımı beyan ve taahhüt ederim.'
];

const getProgramLabel = (programType) => PROGRAM_OPTIONS[programType]?.title || '';
const getApprovalTitle = (programType) => (programType === 'Fitness' ? 'Sağlık Beyanı' : 'Tesis Kullanma Talimatı');
const getApprovalErrorMessage = (programType) => (programType === 'Fitness'
  ? 'Lütfen sağlık beyanını onaylayınız.'
  : 'Lütfen tesis kullanma talimatını onaylayınız.');

const formatVatIncluded = (amount) => `${Number(amount || 0).toFixed(2)} TL (KDV Dahil)`;

const normalizeMembershipPlan = (plan) => ({
  id: plan.id ?? plan.Id,
  programType: plan.programType ?? plan.ProgramType,
  planKey: plan.planKey ?? plan.PlanKey,
  displayName: plan.displayName ?? plan.DisplayName,
  durationMonths: plan.durationMonths ?? plan.DurationMonths,
  price: plan.price ?? plan.Price ?? 0,
  creditCount: plan.creditCount ?? plan.CreditCount ?? 0,
  isActive: plan.isActive ?? plan.IsActive,
  sortOrder: plan.sortOrder ?? plan.SortOrder ?? 0,
});

const CardApplicationForm = () => {
  const [step, setStep] = useState(1);
  const [applicationFee, setApplicationFee] = useState(50.0);
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [paymentData, setPaymentData] = useState(initialPaymentData);
  const [paymentErrors, setPaymentErrors] = useState({});
  const [paymentHtml, setPaymentHtml] = useState(null);
  const [paymentOutcome, setPaymentOutcome] = useState(null);
  const [resultMessage, setResultMessage] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showProgramConfirmModal, setShowProgramConfirmModal] = useState(false);
  const [showPoolGuardianConsentModal, setShowPoolGuardianConsentModal] = useState(false);
  const [guardianConsentData, setGuardianConsentData] = useState({
    guardianFullName: '',
    guardianIdentityNumber: '',
    guardianBirthPlace: '',
    guardianBirthDate: '',
    isAccepted: false
  });
  const [guardianErrors, setGuardianErrors] = useState({});

  const calculateParticipantAge = (birthDateString) => {
    if (!birthDateString) return null;
    const birthDate = new Date(birthDateString);
    if (isNaN(birthDate.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const [resetKey, setResetKey] = useState(0);
  const iframeRef = useRef(null);

  // Crop States
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isCropping, setIsCropping] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      const [feeRes, plansRes] = await Promise.all([
        getCardApplicationFee(),
        getMembershipPlans()
      ]);

      if (feeRes?.isSuccess && feeRes.data?.fee) {
        setApplicationFee(feeRes.data.fee);
      }

      if (plansRes?.isSuccess && Array.isArray(plansRes.data)) {
        setMembershipPlans(plansRes.data.map(normalizeMembershipPlan));
      }
    };

    fetchInitialData();
  }, []);

  const buildProgramSelectionState = useCallback((programType, currentFormData = formData) => {
    const nextPlans = membershipPlans.filter((plan) => plan.programType === programType && plan.isActive);
    const defaultPlan = programType === 'Fitness' ? nextPlans[0] : null;
    // Her iki program için de varsayılan "Sadece Kart" olsun
    return {
      ...currentFormData,
      programType,
      membershipPlanId: defaultPlan ? String(defaultPlan.id) : '',
      subscriptionDuration: defaultPlan ? defaultPlan.displayName : 'Sadece Kart',
      termsAccepted: false
    };
  }, [formData, membershipPlans]);

  const handleProgramSelection = useCallback((programType) => {
    setFormData((prev) => buildProgramSelectionState(programType, prev));
  }, [buildProgramSelectionState]);

  const handleBackToProgramSelection = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      programType: '',
      membershipPlanId: '',
      subscriptionDuration: '',
      termsAccepted: false
    }));
  }, []);

  const availablePlans = membershipPlans.filter((plan) => plan.programType === formData.programType && plan.isActive);
  const selectedMembershipPlan = availablePlans.find((plan) => String(plan.id) === String(formData.membershipPlanId));
  const subscriptionFeeDetails = availablePlans.reduce((acc, plan) => {
    acc[plan.displayName] = {
      fee: plan.price,
      detail: `${plan.creditCount} seans`
    };
    return acc;
  }, {
    'Sadece Kart': { fee: 0, detail: 'Üyelik paketi olmadan sadece kart alımı' }
  });
  const getSubFee = (duration) => subscriptionFeeDetails[duration]?.fee || 0;
  const totalAmount = applicationFee + (selectedMembershipPlan?.price || 0);

  useEffect(() => {
    if (!formData.programType) {
      return;
    }

    if (!formData.membershipPlanId) {
      if (formData.programType !== 'Fitness' && formData.subscriptionDuration === 'Sadece Kart') {
        return;
      }

      const nextPlan = availablePlans.find((plan) => plan.displayName === formData.subscriptionDuration) || availablePlans[0];
      if (nextPlan) {
        setFormData((prev) => ({
          ...prev,
          membershipPlanId: String(nextPlan.id),
          subscriptionDuration: nextPlan.displayName
        }));
      }
      return;
    }

    if (selectedMembershipPlan && formData.subscriptionDuration !== selectedMembershipPlan.displayName) {
      setFormData((prev) => ({ ...prev, subscriptionDuration: selectedMembershipPlan.displayName }));
    }
  }, [availablePlans, formData.membershipPlanId, formData.programType, formData.subscriptionDuration, selectedMembershipPlan]);

  useEffect(() => {
    const handleMessage = (event) => {
      const data = event.data;
      if (!data || data.type !== 'PAYMENT_RESULT') return;

      if (data.status === 'success') {
        setPaymentOutcome('success');
        setResultMessage('Kart başvurunuz başarıyla tamamlandı.');
        setPaymentHtml(null);
      } else if (data.status === 'failed') {
        setPaymentOutcome('failed');
        setResultMessage(data.error ? decodeURIComponent(data.error) : 'Ödeme işlemi başarısız oldu.');
        setPaymentHtml(null);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    try {
      const href = iframe.contentWindow?.location?.href;
      if (!href) return;

      const iframeUrl = new URL(href);
      const paymentStatus = iframeUrl.searchParams.get('paymentStatus');
      const error = iframeUrl.searchParams.get('error');

      if (paymentStatus === 'success') {
        setPaymentOutcome('success');
        setResultMessage('Kart başvurunuz başarıyla tamamlandı.');
        setPaymentHtml(null);
      } else if (paymentStatus === 'failed') {
        setPaymentOutcome('failed');
        setResultMessage(error ? decodeURIComponent(error) : 'Ödeme işlemi başarısız oldu.');
        setPaymentHtml(null);
      }
    } catch {
      // Cross-origin on bank page.
    }
  };

  const luhnCheck = (value) => {
    let checksum = 0;
    let multiplier = 1;

    for (let i = value.length - 1; i >= 0; i -= 1) {
      let product = Number(value.charAt(i)) * multiplier;
      if (product > 9) {
        checksum += 1;
        product -= 10;
      }
      checksum += product;
      multiplier = multiplier === 1 ? 2 : 1;
    }

    return checksum % 10 === 0;
  };

  const detectCardType = (cardNumber) => {
    const cleaned = cardNumber.replace(/\s/g, '');
    if (cleaned.startsWith('4')) return '1';
    if (/^5[1-5]/.test(cleaned) || /^2[2-7]/.test(cleaned)) return '2';
    return null;
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return null;
    }

    // Set canvas size to match the bounding box
    canvas.width = image.width;
    canvas.height = image.height;

    ctx.translate(image.width / 2, image.height / 2);
    ctx.translate(-image.width / 2, -image.height / 2);

    ctx.drawImage(image, 0, 0);

    const data = ctx.getImageData(
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height
    );

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.putImageData(data, 0, 0);

    return canvas.toDataURL('image/jpeg');
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropSave = async () => {
    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
      setFormData({ ...formData, photoBase64: croppedImage });
      setIsCropping(false);
      setImageSrc(null);
    } catch (e) {
      console.error(e);
      toast.error('Fotoğraf kırpılırken bir hata oluştu.');
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Fotoğraf boyutu en fazla 5MB olmalıdır.');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageSrc(reader.result);
        setIsCropping(true);
      };
      reader.readAsDataURL(file);
    }
    // file input u sıfırla ki aynı resmi tekrar seçmek isterse event tetiklensin
    e.target.value = null;
  };

  const handleContinueToPayment = (bypassConsentModal = false) => {
    if (!formData.programType) {
      toast.error('Lütfen önce Havuz veya Fitness seçiniz.');
      return;
    }

    // 1. Zorunlu alanlar kontrolü
    if (!formData.firstName || !formData.lastName || !formData.identityNumber || !formData.phone || !formData.userGender || !formData.profession || !formData.homeAddress || !formData.birthDate || !formData.photoBase64) {
      toast.error('Lütfen tüm zorunlu alanları ve fotoğrafı doldurunuz.');
      return;
    }

    if (!formData.termsAccepted) {
      toast.error(getApprovalErrorMessage(formData.programType));
      return;
    }

    if (formData.programType === 'Fitness' && !selectedMembershipPlan) {
      toast.error('Fitness için aktif bir üyelik paketi bulunamadı.');
      return;
    }

    if (formData.identityNumber.length !== 11) {
      toast.error('TC Kimlik No 11 haneli olmalıdır.');
      return;
    }

    if (formData.phone.length !== 10 || !formData.phone.startsWith('5')) {
      toast.error('Geçerli bir telefon numarası giriniz (5XXXXXXXXX).');
      return;
    }

    // 2. Yaş ve Veli İzin Formu kontrolü
    const participantAge = calculateParticipantAge(formData.birthDate);

    if (formData.programType === 'Fitness' && participantAge !== null && participantAge < 16) {
      toast.error('Fitness kart başvurusu için en az 16 yaşında olunmalıdır.');
      return;
    }

    if (formData.programType === 'Pool' && !bypassConsentModal) {
      const needsConsent = participantAge === null || (participantAge >= 6 && participantAge < 18);
      if (needsConsent) {
        if (!guardianConsentData.isAccepted || !guardianConsentData.guardianFullName || !guardianConsentData.guardianIdentityNumber) {
          setShowPoolGuardianConsentModal(true);
          toast.warn('Havuz başvuruları için Veli İzin Formu ve Sağlık Beyanı doldurulup onaylanmalıdır.');
          return;
        }
      }
    }

    // 3. Tüm kontroller geçti → program onay modalı
    setShowProgramConfirmModal(true);
  };

  const confirmProgramAndContinueToPayment = () => {
    setShowProgramConfirmModal(false);
    setStep(2);
  };

  const handlePayment = async () => {
    setLoading(true);

    const errors = {};
    const pan = paymentData.cardNumber.replace(/\s/g, '');
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    if (!pan) {
      errors.cardNumber = 'Kart numarası gereklidir.';
    } else if (pan.length !== 16) {
      errors.cardNumber = 'Kart numarası 16 haneli olmalıdır.';
    } else if (!luhnCheck(pan)) {
      errors.cardNumber = 'Geçersiz kart numarası.';
    }

    if (!paymentData.cardName.trim()) {
      errors.cardName = 'Kart üzerindeki isim gereklidir.';
    }

    if (!paymentData.expiryMonth || !paymentData.expiryYear) {
      errors.expiry = 'Son kullanma tarihi seçiniz.';
    } else {
      const expYear = parseInt(paymentData.expiryYear, 10);
      const expMonth = parseInt(paymentData.expiryMonth, 10);
      if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
        errors.expiry = 'Kartın son kullanma tarihi geçmiş.';
      }
    }

    if (!paymentData.cvv) {
      errors.cvv = 'CVV gereklidir.';
    } else if (paymentData.cvv.length !== 3) {
      errors.cvv = 'CVV 3 haneli olmalıdır.';
    }

    if (Object.keys(errors).length > 0) {
      setPaymentErrors(errors);
      setLoading(false);
      return;
    }

    setPaymentErrors({});

    try {
      const initResult = await initiateApplicationPayment({
        programType: formData.programType,
        firstName: formData.firstName,
        lastName: formData.lastName,
        identityNumber: formData.identityNumber,
        email: formData.email || '',
        phone: formData.phone,
        phone2: formData.phone2,
        userGender: formData.userGender,
        profession: formData.profession,
        homeAddress: formData.homeAddress,
        membershipPlanId: selectedMembershipPlan?.id || null,
        subscriptionDuration: formData.subscriptionDuration,
        birthDate: formData.birthDate,
        photoBase64: formData.photoBase64,
        pan,
        cv2: paymentData.cvv,
        expireMonth: paymentData.expiryMonth.padStart(2, '0'),
        expireYear: paymentData.expiryYear,
        cardType: detectCardType(paymentData.cardNumber),
        
        guardianFullName: guardianConsentData.guardianFullName || null,
        guardianIdentityNumber: guardianConsentData.guardianIdentityNumber || null,
        guardianBirthPlace: guardianConsentData.guardianBirthPlace || null,
        guardianBirthDate: guardianConsentData.guardianBirthDate || null,
        isGuardianConsentAccepted: guardianConsentData.isAccepted || false
      });

      if (!initResult?.isSuccess) {
        toast.error(initResult?.resultMessage || 'Başvuru oluşturulamadı.');
        return;
      }

      const paymentPayload = {
        amount: initResult.data.amount ?? totalAmount,
        paymentGroup: 'CardApplicationFee',
        referenceId: String(initResult.data.transactionId),
        applicationId: initResult.data.applicationId,
        description: initResult.data.description || `Üyelik Başvurusu (${formData.subscriptionDuration}) - ${formData.firstName} ${formData.lastName}`,
        programType: formData.programType,
        membershipPlanId: selectedMembershipPlan?.id || null,
        pan,
        cv2: paymentData.cvv,
        expireMonth: paymentData.expiryMonth.padStart(2, '0'),
        expireYear: paymentData.expiryYear,
        cardType: detectCardType(paymentData.cardNumber),
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email || ''
      };

      const paymentResult = await initiateGeneralPayment(paymentPayload);

      if (paymentResult?.isSuccess && paymentResult?.data?.html) {
        setPaymentHtml(paymentResult.data.html);
        setStep(3);
      } else {
        toast.error(paymentResult?.resultMessage || 'Ödeme başlatılamadı.');
      }
    } catch (error) {
      console.error(error);
      toast.error('Ödeme sırasında beklenmeyen bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setFormData(initialFormData);
    setPaymentData(initialPaymentData);
    setPaymentErrors({});
    setPaymentHtml(null);
    setPaymentOutcome(null);
    setResultMessage('');
    setResetKey(prev => prev + 1);
  };

  const previewCardNumber = paymentData.cardNumber || '0000 0000 0000 0000';
  const fallbackCardHolder = `${formData.firstName} ${formData.lastName}`.trim();
  const previewCardHolder = paymentData.cardName || fallbackCardHolder || 'AD SOYAD';
  const previewExpiry = paymentData.expiryMonth && paymentData.expiryYear
    ? `${paymentData.expiryMonth}/${String(paymentData.expiryYear).slice(-2)}`
    : 'AA/YY';
  const selectedProgramLabel = getProgramLabel(formData.programType);
  const approvalTitle = getApprovalTitle(formData.programType);
  return (
    <div className="sa-card-application">
      <div className="sa-facility-details-header sa-unified-tab-header">
        <div className="sa-unified-tab-header-row">
          <h3 className="sa-unified-tab-title">
            <div className="sa-unified-tab-icon">
              <FaIdCard size={20} />
            </div>
            {selectedProgramLabel ? `${selectedProgramLabel} Üyelik Başvuru Formu` : 'Üyelik Başvuru Formu'}
          </h3>
          {step === 1 && formData.programType && (
            <div className="sa-unified-tab-price-breakdown">
              <span className="sa-price-item">Kart: {formatVatIncluded(applicationFee)}</span>
              {formData.subscriptionDuration !== 'Sadece Kart' && (
                <>
                  <span className="sa-price-separator">+</span>
                  <span className="sa-price-item">Üyelik: {formatVatIncluded(getSubFee(formData.subscriptionDuration))}</span>
                </>
              )}
              <span className="sa-price-total">Top: {formatVatIncluded(totalAmount)}</span>
            </div>
          )}
        </div>
        <p className="sa-unified-tab-subtitle">
          {formData.programType
            ? 'Bilgilerinizi doldurup ödeme adımına geçebilirsiniz.'
            : 'Önce kart başvurusu yapmak istediğiniz tesisi seçiniz.'}
        </p>
      </div>

      <div style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>

        <div className="sa-stepper sa-card-stepper" style={{ marginBottom: '30px' }}>
          <div className={`sa-step ${step === 1 ? 'active' : step > 1 ? 'completed' : ''}`}>
            <span className="sa-step-circle">1</span>
            <span className="sa-step-label">Bilgiler</span>
            <div className="sa-step-line"></div>
          </div>
          <div className={`sa-step ${step === 2 ? 'active' : step > 2 ? 'completed' : ''}`}>
            <span className="sa-step-circle">2</span>
            <span className="sa-step-label">Ödeme</span>
            <div className="sa-step-line"></div>
          </div>
          <div className={`sa-step ${step >= 3 ? 'active' : ''}`}>
            <span className="sa-step-circle">3</span>
            <span className="sa-step-label">Sonuç</span>
          </div>
        </div>

        {step === 1 ? (
          <>
            <div className="sa-appointment-section">
              {formData.programType ? (
                <>
                  <div className="sa-booking-form sa-card-form" style={{ marginTop: '20px', border: 'none', background: 'transparent', boxShadow: 'none', padding: '10px 12px 14px' }}>
                    <div className="sa-card-program-summary">
                      <div className="sa-card-program-summary__badge">
                        {PROGRAM_OPTIONS[formData.programType]?.icon && React.createElement(PROGRAM_OPTIONS[formData.programType].icon, { size: 18 })}
                        <span>{selectedProgramLabel} Başvurusu</span>
                      </div>
                      <button type="button" className="sa-card-program-summary__change" onClick={handleBackToProgramSelection}>
                        Havuz / Fitness Değiştir
                      </button>
                    </div>

                    <h4 style={{ fontSize: '0.85rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ width: '24px', height: '3px', background: '#e2e8f0', borderRadius: '2px', display: 'block' }}></span>
                      Başvuru Bilgileri
                    </h4>
                    <div className="sa-form-grid">
                      <div className="sa-form-group">
                        <label>Ad <span className="sa-required">*</span></label>
                        <input className="sa-form-input" value={formData.firstName} onChange={(event) => setFormData({ ...formData, firstName: event.target.value })} />
                      </div>
                      <div className="sa-form-group">
                        <label>Soyad <span className="sa-required">*</span></label>
                        <input className="sa-form-input" value={formData.lastName} onChange={(event) => setFormData({ ...formData, lastName: event.target.value })} />
                      </div>
                      <div className="sa-form-group">
                        <label>TC Kimlik No <span className="sa-required">*</span></label>
                        <input
                          className="sa-form-input"
                          maxLength={11}
                          value={formData.identityNumber}
                          onChange={(event) => setFormData({ ...formData, identityNumber: event.target.value.replace(/\D/g, '').slice(0, 11) })}
                        />
                      </div>
                      <div className="sa-form-group">
                        <label>Telefon <span className="sa-required">*</span></label>
                        <input
                          className="sa-form-input"
                          value={formData.phone}
                          maxLength={10}
                          placeholder="5XXXXXXXXX"
                          onChange={(event) => setFormData({ ...formData, phone: event.target.value.replace(/\D/g, '').slice(0, 10) })}
                        />
                      </div>
                      <div className="sa-form-group">
                        <label>Cep Telefonu 2 (Opsiyonel)</label>
                        <input
                          className="sa-form-input"
                          value={formData.phone2}
                          maxLength={10}
                          placeholder="5XXXXXXXXX"
                          onChange={(event) => setFormData({ ...formData, phone2: event.target.value.replace(/\D/g, '').slice(0, 10) })}
                        />
                      </div>
                      <div className="sa-form-group">
                        <label>E-posta</label>
                        <input className="sa-form-input" value={formData.email} onChange={(event) => setFormData({ ...formData, email: event.target.value })} />
                      </div>
                      <div className="sa-form-group">
                        <label>Cinsiyet <span className="sa-required">*</span></label>
                        <select className="sa-form-input" value={formData.userGender} onChange={(event) => setFormData({ ...formData, userGender: event.target.value })}>
                          <option value="">Seçiniz</option>
                          <option value="Male">Erkek</option>
                          <option value="Female">Kadın</option>
                        </select>
                      </div>
                      <div className="sa-form-group">
                        <label>Meslek <span className="sa-required">*</span></label>
                        <input className="sa-form-input" value={formData.profession} onChange={(event) => setFormData({ ...formData, profession: event.target.value })} />
                      </div>
                      <div className="sa-form-group">
                        <label>Doğum Tarihi <span className="sa-required">*</span></label>
                        <input type="date" className="sa-form-input" value={formData.birthDate} max={new Date().toISOString().split('T')[0]} onChange={(event) => setFormData({ ...formData, birthDate: event.target.value })} />
                      </div>
                      <div className="sa-form-group">
                        <label>Üyelik Paketi <span className="sa-required">*</span></label>
                        <select className="sa-form-input" value={formData.membershipPlanId} onChange={(event) => {
                          const membershipPlanId = event.target.value;
                          const plan = availablePlans.find((item) => String(item.id) === membershipPlanId);
                          setFormData({
                            ...formData,
                            membershipPlanId,
                            subscriptionDuration: membershipPlanId ? (plan?.displayName || '') : 'Sadece Kart'
                          });
                        }}>
                          {formData.programType === 'Pool' && <option value="">Sadece Kart</option>}
                          {availablePlans.map((plan) => (
                            <option key={plan.id} value={String(plan.id)}>
                              {plan.displayName} (+{formatVatIncluded(plan.price)} / {plan.creditCount} seans)
                            </option>
                          ))}
                        </select>
                        <small style={{ color: '#0f75bc', fontWeight: '600', marginTop: '4px' }}>
                          {subscriptionFeeDetails[formData.subscriptionDuration]?.detail}
                        </small>
                      </div>
                      <div className="sa-form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Ev Adresi <span className="sa-required">*</span></label>
                        <textarea
                          className="sa-form-input"
                          rows="3"
                          value={formData.homeAddress}
                          onChange={(event) => setFormData({ ...formData, homeAddress: event.target.value })}
                          style={{ resize: 'vertical' }}
                        ></textarea>
                      </div>
                      <div className="sa-form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Vesikalık / Profil Fotoğrafı <span className="sa-required">*</span></label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                          {formData.photoBase64 && (
                            <img src={formData.photoBase64} alt="Preview" style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #3b82f6' }} />
                          )}
                          <input type="file" accept="image/*" onChange={handlePhotoUpload} className="sa-form-input" style={{ flex: 1, padding: '8px' }} />
                        </div>
                        <small style={{ color: '#64748b', display: 'block', marginTop: '5px' }}>Lütfen yüzünüzün net göründüğü bir fotoğraf yükleyiniz (Maks 5MB).</small>
                      </div>

                      <div className="sa-form-group" style={{ gridColumn: '1 / -1', marginTop: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            id="termsAccepted"
                            checked={formData.termsAccepted}
                            onChange={(e) => setFormData({ ...formData, termsAccepted: e.target.checked })}
                            style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                          />
                          <label htmlFor="termsAccepted" style={{ fontSize: '0.9rem', color: '#334155', cursor: 'pointer', fontWeight: '600' }}>
                            <span onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }} style={{ color: '#0f75bc', textDecoration: 'underline' }}>
                              {approvalTitle}
                            </span>
                            {' '}metnini okudum ve onaylıyorum.
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Veli İzin Formu durumu — sadece Havuz başvurusunda göster */}
                  {formData.programType === 'Pool' && (() => {
                    const age = calculateParticipantAge(formData.birthDate);
                    const needsConsent = age === null || (age >= 6 && age < 18);
                    if (!needsConsent) return null;
                    const filled = guardianConsentData.isAccepted && guardianConsentData.guardianFullName && guardianConsentData.guardianIdentityNumber;
                    return (
                      <div style={{
                        margin: '12px 0 4px',
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: filled ? '1.5px solid #22c55e' : '1.5px dashed #f59e0b',
                        background: filled ? '#f0fdf4' : '#fffbeb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px',
                        flexWrap: 'wrap'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {filled
                            ? <FaCheckCircle size={20} style={{ color: '#22c55e', flexShrink: 0 }} />
                            : <FaExclamationTriangle size={20} style={{ color: '#f59e0b', flexShrink: 0 }} />
                          }
                          <div>
                            <p style={{ margin: 0, fontWeight: '700', fontSize: '0.85rem', color: filled ? '#15803d' : '#b45309' }}>
                              {filled ? 'Veli İzin Formu Dolduruldu' : 'Veli İzin Formu Gerekli'}
                            </p>
                            {filled ? (
                              <p style={{ margin: 0, fontSize: '0.78rem', color: '#166534' }}>
                                {guardianConsentData.guardianFullName} · {guardianConsentData.guardianIdentityNumber}
                              </p>
                            ) : (
                              <p style={{ margin: 0, fontSize: '0.78rem', color: '#92400e' }}>
                                6-18 yaş arası havuz başvurusu için veli izni zorunludur.
                              </p>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowPoolGuardianConsentModal(true)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '7px 16px',
                            borderRadius: '8px',
                            border: filled ? '1px solid #22c55e' : '1px solid #f59e0b',
                            background: filled ? '#dcfce7' : '#fef3c7',
                            color: filled ? '#15803d' : '#b45309',
                            fontWeight: '700',
                            fontSize: '0.82rem',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {filled
                            ? <><FaEdit size={13} /> Düzenle</>
                            : <><FaClipboardList size={13} /> Formu Doldur</>
                          }
                        </button>
                      </div>
                    );
                  })()}

                  <div className="sa-action-row">
                    <button type="button" className="sa-submit-button" onClick={() => handleContinueToPayment()}>
                      Ödemeye Geç
                    </button>
                  </div>
                </>
              ) : (
                <div className="sa-booking-form sa-card-form" style={{ marginTop: '20px', border: 'none', background: 'transparent', boxShadow: 'none', padding: '10px 12px 14px' }}>
                  <div className="sa-card-program-selector">
                    <h4 style={{ fontSize: '0.85rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ width: '24px', height: '3px', background: '#e2e8f0', borderRadius: '2px', display: 'block' }}></span>
                      Önce Tesis Seçimi Yapın
                    </h4>
                    <p className="sa-card-program-selector__lead">
                      Kart başvurusu için önce hangi tesise başvuracağınızı seçin. Sonraki adımda sadece o tesise ait paketler görünecek.
                    </p>
                    <div className="sa-card-program-selector__grid">
                      {Object.entries(PROGRAM_OPTIONS).map(([programType, option]) => {
                        const Icon = option.icon;
                        const programPlans = membershipPlans.filter((plan) => plan.programType === programType && plan.isActive);
                        const priceLine = programPlans.length > 0
                          ? programPlans.map((plan) => `${plan.displayName}: ${formatVatIncluded(plan.price)}`).join(' / ')
                          : 'Aktif paket bulunamadı';

                        return (
                          <button
                            key={programType}
                            type="button"
                            className="sa-card-program-selector__option"
                            onClick={() => handleProgramSelection(programType)}
                          >
                            <div className="sa-card-program-selector__icon">
                              <Icon size={24} />
                            </div>
                            <strong>{option.title}</strong>
                            <span>{option.description}</span>
                            <small>{option.detail}</small>
                            <div className="sa-card-program-selector__price">{priceLine}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {isCropping && (
                <div style={cropModalOverlayStyle}>
                  <div style={cropModalStyle}>
                    <div style={{ position: 'relative', width: '100%', height: 'calc(100% - 60px)', background: '#333' }}>
                      <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        aspect={3 / 4} // Vesikalık standart 3:4 orani
                        onCropChange={setCrop}
                        onCropComplete={onCropComplete}
                        onZoomChange={setZoom}
                      />
                    </div>
                    <div style={cropControlsStyle}>
                      <input
                        type="range"
                        value={zoom}
                        min={1}
                        max={3}
                        step={0.1}
                        aria-labelledby="Zoom"
                        onChange={(e) => setZoom(e.target.value)}
                        style={{ flex: 1, margin: '0 10px' }}
                      />
                      <button onClick={(e) => { e.preventDefault(); setIsCropping(false); setImageSrc(null); }} style={cropBtnCancel}>İptal</button>
                      <button onClick={(e) => { e.preventDefault(); handleCropSave(); }} style={cropBtnSave}>Kırp ve Kaydet</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : step === 2 ? (
          <div className="sa-appointment-section">
            <div className="sa-booking-form sa-card-form" style={{ marginTop: '20px', border: 'none', background: 'transparent', boxShadow: 'none', padding: '10px 12px 14px' }}>
              <h4 className="sa-modern-section-title">
                <span className="sa-modern-section-line"></span>
                Ödeme Bilgileri
              </h4>

              <div className="sa-modern-payment-shell sa-card-payment-shell">
                <div className="sa-modern-payment-meta">
                  <div className="sa-modern-payment-meta-item">
                    <span className="sa-modern-payment-meta-label">Ücret Detayı</span>
                    <strong className="sa-modern-payment-meta-value" style={{ fontSize: '0.85rem' }}>
                      {formData.subscriptionDuration === 'Sadece Kart'
                        ? `Sadece Kart: ${formatVatIncluded(applicationFee)}`
                        : `Kart: ${formatVatIncluded(applicationFee)} + Üyelik: ${formatVatIncluded(getSubFee(formData.subscriptionDuration))}`
                      }
                    </strong>
                  </div>
                  <div className="sa-modern-payment-meta-item sa-modern-payment-meta-item--amount">
                    <span className="sa-modern-payment-meta-label">Ödenecek Toplam</span>
                    <strong className="sa-modern-payment-meta-value">{formatVatIncluded(totalAmount)}</strong>
                  </div>
                </div>

                <div className="sa-modern-payment-layout">
                  <div className="sa-modern-card-preview sa-modern-card-preview--card-app">
                    <span className="sa-modern-card-chip" aria-hidden="true"></span>
                    <div className="sa-modern-card-number">{previewCardNumber}</div>
                    <div className="sa-modern-card-footer">
                      <div>
                        <span>Kart Sahibi</span>
                        <strong>{previewCardHolder}</strong>
                      </div>
                      <div className="sa-modern-card-expiry">
                        <span>SKT</span>
                        <strong>{previewExpiry}</strong>
                      </div>
                    </div>
                    <div className="sa-modern-card-brand">
                      {detectCardType(paymentData.cardNumber) === '1' && <FaCcVisa size={28} color="#f8fafc" />}
                      {detectCardType(paymentData.cardNumber) === '2' && <FaCcMastercard size={28} color="#fed7aa" />}
                      {!detectCardType(paymentData.cardNumber) && <FaCreditCard size={24} color="#dbeafe" />}
                    </div>
                  </div>

                  <div className="sa-modern-payment-form">
                    <div className="sa-form-grid sa-modern-payment-grid">
                      <div className="sa-form-group sa-modern-payment-wide">
                        <label>Kart Numarası</label>
                        <div className="sa-input-with-icon">
                          <input
                            className="sa-form-input"
                            value={paymentData.cardNumber}
                            maxLength={19}
                            inputMode="numeric"
                            placeholder="0000 0000 0000 0000"
                            onChange={(event) => {
                              const raw = event.target.value.replace(/\D/g, '').slice(0, 16);
                              const formatted = raw.match(/.{1,4}/g)?.join(' ') || raw;
                              setPaymentData({ ...paymentData, cardNumber: formatted });
                            }}
                          />
                          <div className="sa-card-brand-icon">
                            {detectCardType(paymentData.cardNumber) === '1' && <FaCcVisa size={24} color="#1a1f71" />}
                            {detectCardType(paymentData.cardNumber) === '2' && <FaCcMastercard size={24} color="#f97316" />}
                            {!detectCardType(paymentData.cardNumber) && <FaCreditCard size={22} color="#94a3b8" />}
                          </div>
                        </div>
                        {paymentErrors.cardNumber && <div className="sa-error-message">{paymentErrors.cardNumber}</div>}
                      </div>

                      <div className="sa-form-group sa-modern-payment-wide">
                        <label>Kart Üzerindeki İsim</label>
                        <input
                          className="sa-form-input"
                          value={paymentData.cardName}
                          placeholder="AD SOYAD"
                          onChange={(event) => setPaymentData({ ...paymentData, cardName: event.target.value.toUpperCase() })}
                        />
                        {paymentErrors.cardName && <div className="sa-error-message">{paymentErrors.cardName}</div>}
                      </div>

                      <div className="sa-form-group">
                        <label>Ay</label>
                        <select className="sa-form-input" value={paymentData.expiryMonth} onChange={(event) => setPaymentData({ ...paymentData, expiryMonth: event.target.value })}>
                          <option value="">Ay</option>
                          {Array.from({ length: 12 }, (_, index) => {
                            const month = String(index + 1).padStart(2, '0');
                            return <option key={month} value={month}>{month}</option>;
                          })}
                        </select>
                      </div>

                      <div className="sa-form-group">
                        <label>Yıl</label>
                        <select className="sa-form-input" value={paymentData.expiryYear} onChange={(event) => setPaymentData({ ...paymentData, expiryYear: event.target.value })}>
                          <option value="">Yıl</option>
                          {Array.from({ length: 10 }, (_, index) => {
                            const year = String(new Date().getFullYear() + index);
                            return <option key={year} value={year}>{year}</option>;
                          })}
                        </select>
                      </div>

                      <div className="sa-form-group">
                        <label>CVV</label>
                        <input
                          className="sa-form-input"
                          type="password"
                          maxLength={3}
                          inputMode="numeric"
                          value={paymentData.cvv}
                          placeholder="123"
                          onChange={(event) => setPaymentData({ ...paymentData, cvv: event.target.value.replace(/\D/g, '').slice(0, 3) })}
                        />
                        {paymentErrors.cvv && <div className="sa-error-message">{paymentErrors.cvv}</div>}
                      </div>
                    </div>

                    {paymentErrors.expiry && <div className="sa-error-message">{paymentErrors.expiry}</div>}
                  </div>
                </div>
              </div>

              <div className="sa-action-row sa-card-action-row">
                <button
                  type="button"
                  className="sa-btn-cancel"
                  onClick={() => {
                    setStep(1);
                    setPaymentData(initialPaymentData);
                    setPaymentErrors({});
                  }}
                  disabled={loading}
                >
                  Geri
                </button>
                <button type="button" className="sa-submit-button" onClick={handlePayment} disabled={loading}>
                  {loading ? 'İşleniyor...' : 'Ödemeyi Tamamla'}
                </button>
              </div>
            </div>
          </div>
        ) : paymentOutcome ? (
          <div className={`sa-payment-result sa-card-result ${paymentOutcome === 'success' ? 'sa-payment-success' : 'sa-payment-failed'}`}>
            <div className={`sa-result-icon ${paymentOutcome === 'failed' ? 'error' : ''}`}>
              {paymentOutcome === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
            </div>
            <h2>{paymentOutcome === 'success' ? 'Başvuru Alındı' : 'Ödeme Başarısız'}</h2>
            {paymentOutcome === 'success' ? (
              <div style={{ marginTop: '20px', padding: '24px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                {formData.programType === 'Fitness' ? (
                  <div style={{ padding: '16px', background: '#fff', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                    <p style={{ color: '#0f172a', fontSize: '1.05rem', fontWeight: '600', marginBottom: '8px' }}>
                      Ön başvurunuz alınmıştır.
                    </p>
                    <p style={{ margin: 0, color: '#64748b', fontSize: '0.95rem', lineHeight: '1.6' }}>
                      Fitness üyelik işlemlerini ve kart tanımlamasını tamamlamak için lütfen ilgili fitness tesisimize başvurunuz.
                    </p>
                  </div>
                ) : (
                  <div style={{ padding: '16px', background: '#fff', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                    <p style={{ color: '#0f172a', fontSize: '1.05rem', fontWeight: '600', marginBottom: '16px' }}>Ön başvurunuz alınmıştır.<br />Üyelik işlemlerini tamamlamak için lütfen Sakarya İskenderun Belediyesi Yarı Olimpik Kapalı Yüzme Havuzu tesisimize geliniz.</p>
                    <h4 style={{ margin: '0 0 8px 0', color: '#334155', fontSize: '1rem' }}>Tesis Adresimiz</h4>
                    <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem', lineHeight: '1.5' }}>
                      Sakarya, Şht. Astsb. Kd. Çvş. Ali Ermercan Cd. İskenderun/Hatay
                    </p>
                    <a href="https://maps.google.com/?q=İskenderun+Belediyesi+Yarı+Olimpik+Kapalı+Yüzme+Havuzu" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', marginTop: '12px', color: '#3b82f6', textDecoration: 'none', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      Haritada Gör
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <p className={`sa-result-message ${paymentOutcome === 'failed' ? 'error' : ''}`}>{resultMessage}</p>
            )}
            <div className="sa-action-row sa-card-result-actions">
              {paymentOutcome === 'failed' && (
                <button type="button" className="sa-submit-button sa-payment-action" data-variant="positive" onClick={() => { setPaymentOutcome(null); setStep(2); }}>
                  Tekrar Dene
                </button>
              )}
              <button type="button" className="sa-btn-cancel" onClick={resetForm}>
                Yeni Başvuru
              </button>
            </div>
          </div>
        ) : paymentHtml ? (
          <div className="sa-card-iframe-card">
            <div className="sa-card-iframe-header">
              <button type="button" className="sa-card-iframe-back" onClick={() => { setPaymentHtml(null); setStep(2); }}>
                <FaArrowLeft /> Geri
              </button>
              <strong>{formatVatIncluded(totalAmount)}</strong>
            </div>
            <iframe
              ref={iframeRef}
              title="3D Secure Payment"
              srcDoc={paymentHtml}
              onLoad={handleIframeLoad}
              className="sa-card-iframe"
            />
          </div>
        ) : (
          <div className="sa-card-loading">Ödeme sayfası yükleniyor...</div>
        )}
      </div>

      {showProgramConfirmModal && (
        <div className="sa-terms-modal-overlay" onClick={() => setShowProgramConfirmModal(false)}>
          <div className="sa-terms-modal" onClick={(event) => event.stopPropagation()}>
            <div className="sa-terms-modal-header">
              <h3>Spor Türü Onayı</h3>
              <button className="sa-terms-modal-close" onClick={() => setShowProgramConfirmModal(false)}>&times;</button>
            </div>
            <div className="sa-terms-modal-body">
              <p style={{ margin: 0, color: '#334155', fontSize: '1rem', lineHeight: '1.7' }}>
                Aldığınız üyelik başvurusu <strong>{selectedProgramLabel}</strong> üyeliğine aittir.
              </p>
              <p style={{ marginTop: '12px', color: '#64748b', fontSize: '0.95rem', lineHeight: '1.7' }}>
                Üyelik aktarımı yapılmamaktadır. Devam etmek istediğinizden emin misiniz?
              </p>
              {formData.programType === 'Pool' && (
                <p style={{ marginTop: '12px', color: '#64748b', fontSize: '0.95rem', lineHeight: '1.7', fontWeight: 700 }}>
                  *Bu işlemi yaparak yüzme bildiğinizi onaylarsınız.
                </p>
              )}
            </div>
            <div className="sa-terms-modal-footer">
              <button className="sa-submit-button" onClick={confirmProgramAndContinueToPayment}>Eminim, Devam Et</button>
              <button className="sa-btn-cancel" onClick={() => setShowProgramConfirmModal(false)}>Vazgeç</button>
            </div>
          </div>
        </div>
      )}

      {showTermsModal && (
        <div className="sa-terms-modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="sa-terms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sa-terms-modal-header">
              <h3>{approvalTitle}</h3>
              <button className="sa-terms-modal-close" onClick={() => setShowTermsModal(false)}>&times;</button>
            </div>
            <div className="sa-terms-modal-body">
              <div className="sa-terms-content">
                {formData.programType === 'Fitness' ? (
                  <>
                    <center><strong>{FITNESS_HEALTH_DECLARATION_TITLE}</strong></center>
                    <br />
                    {FITNESS_HEALTH_DECLARATION_PARAGRAPHS.map((paragraph) => (
                      <p key={paragraph} style={{ marginBottom: '14px', lineHeight: '1.7' }}>{paragraph}</p>
                    ))}
                    <p><strong>Okudum, anladım.</strong></p>
                  </>
                ) : (
                  <>
                    <center><strong>T.C. İSKENDERUN BELEDİYESİ<br />YARI OLİMPİK KAPALI YÜZME HAVUZU TESİS KULLANMA TALİMATI</strong></center>
                    <br />
                    <strong>GENEL KURALLLAR</strong>
                    <ol>
                      <li>Havuza bonesiz, mayosuz, terliksiz ve duş alınmadan kesinlikle girilemez. Bu şartları sağlamayanlar havuz dışına çıkartılır.</li>
                      <li>Havuza girişte dezenfektanlı suya ayakların batırılması hijyen açısından zorunludur.</li>
                      <li>Havuz bölgesine, su dışında (matara) yiyecek ve içecek sokulması yasaktır.</li>
                      <li>Sporcu ve öğrencilerin, tribünde soyunup giyinmesi kesinlikle yasaktır.</li>
                      <li>Velilerin soyunma odasına girmesi kesinlikle yasaktır.</li>
                      <li>Açık ve kanamalı yarası, bandajı, kesiği, mantar gibi cilt hastalığı ve bulaşıcı hastalık hallerinde kesinlikle havuza girilemez.</li>
                      <li>Tesise cinsi ve niteliği ne olursa olsun hiçbir hayvanla girilemez.</li>
                      <li>Tesis sınırları içerisinde personel, üye ve misafirler dahil olmak üzere alkollü giriş yapılamayacağı gibi her türlü alkol ve sigara kullanımı yasaktır.</li>
                      <li>Yanlarında kıymetli evrak ve maddi değeri yüksek eşya vb. bulunduran kişiler bu eşyaların kaybından kendileri sorumludur. Kaybedilen hiçbir şeyden tesis yönetimi sorumlu tutulamaz.</li>
                      <li>Üye ve misafirler istenildiği takdirde ilgili personel ve güvenlik görevlilerine üye kartını ve kimliğini ibraz etmek zorundadır.</li>
                      <li>Üye ve misafirler yararlandığı spor branşına göre uygun ekipmanları bulundurmakla yükümlüdür. Aksi halde hizmetten faydalanmayı talep edemez.</li>
                      <li>Bayanların hizmetine açık olan gün ve saatlerde tribün seyirciye kapalıdır.</li>
                      <li>Görevi ve ünvanı ne olursa olsun, tesisten ve hizmetlerden bedelini ödemeden hiç kimse faydalanamaz.</li>
                      <li>Yüzme bilmeyenlerin antrenörsüz ve can kurtaransız havuza girmesi yasaktır.</li>
                      <li>Havuz merdivenlerine oturulamaz.</li>
                      <li>Üyeler ve kursiyerler mayo ile tribüne çıkamazlar.</li>
                      <li>Dışarıda kullanılan ayakkabı ve terlikle havuz çevresinde kesinlikle dolaşılmaz.</li>
                      <li>Havuz çevresinde koşmak, itişmek ve tehlike oluşturacak şekilde atlamak yasaktır.</li>
                      <li>On sekiz yaşından küçük çocuklar veli onayı olmaksızın yüzme havuzundan faydalanamaz.</li>
                      <li>Tesis kurallarına uymayan kişilerin üyelikleri iptal edilecektir. Ödenen ücretlerin geri iadesi yapılmayacaktır.</li>
                      <li>Eğitim ve çalışma saatinin belirlenmesi idare yetkisinde olup, idare bu eğitim saatlerini değiştirme ve iptal etme hakkına sahiptir.</li>
                      <li>Üyelik veya eğitim formunu doldurup imza atan tüm kullanıcılarımız aynı zamanda tesis kurallarına uyacağına dair imza atmış sayılır.</li>
                      <li>Kulvarın üzerine oturulmaz. Aksi belirtilmedikçe havuz enine kullanılamaz.</li>
                      <li>Değerli üyelerimiz ve kursiyerlerimizin daha sağlıklı, daha güvenli, daha temiz bir ortamda spor yapması için tesis kurallarına uyunuz, uymayanları uyarınız.</li>
                      <li>Kullanıcıların havuz içerisinde ve çevresinde bulunan malzemelerde meydana getirecekleri hasardan kendileri sorumludur.</li>
                      <li>Seyirciler havuz içerisine inemezler.</li>
                      <li>Eğitim seanslarında ilk 10 dakika içeride kalınabilir, sonrasında ebeveynler dışarıda beklemelidir.</li>
                      <li>Deniz malzemeleri (deniz topu, deniz yatağı, deniz simidi, kolluk) kullanmak yasaktır.</li>
                      <li>Genel temizlik günü havuz kapalıdır.</li>
                    </ol>

                    <strong>HAVUZ GİRİŞİ</strong>
                    <ul>
                      <li>Dışarıda kullanılan ayakkabılarla havuz çevresine girilmesi yasaktır.</li>
                      <li>Havuz kullanımı öncesi duş alınması zorunludur. Duşlarda şampuan, sabun vb. malzeme kullanılamaz. Duş almadan havuzu kullanan bir kişi 20 m³ suyu kirletir, duş alırsa bu miktar 2 m³ suya düşer.</li>
                      <li>Havuza girmeden önce her türlü ziynet eşyasının (kolye, yüzük vb.) çıkarılması zorunludur.</li>
                      <li>Havuza girmeden önce varsa ağzınızdaki sakızı çıkarmanız can güvenliğiniz açısından önemlidir.</li>
                      <li>Havuz suyuna tükürmek, havuz suyu ile ağız ve burun temizlemek yasaktır.</li>
                      <li>Yüzme bilmeyenlerin, havuz eğitmenlerinin bilgisi dışında kullanması yasaktır.</li>
                    </ul>

                    <strong>HAVUZ ÇIKIŞINDA</strong>
                    <ul>
                      <li>Bitim anonsuyla birlikte havuzu mümkün olduğunca çabuk boşaltınız ve kesinlikle tekrar havuza girmeyiniz.</li>
                      <li>Duşları fazla meşgul etmeyiniz ve soyunma odasını zamanında terk ediniz.</li>
                      <li>Çıkış esnasında havuzu terlikle terk ediniz ve içeride kesinlikle ayakkabı giymeyiniz.</li>
                    </ul>

                    <strong>SOYUNMA ODALARI KULLANMA TALİMATI</strong>
                    <ul>
                      <li>Dolaplar sadece "Günlük Kullanım" içindir, çalışmanız bittikten sonra eşyalarınızı alınız.</li>
                      <li>Para veya değerli eşyalarınızı dolaplara bırakmayınız, bu konuda mesuliyet kabul edilmez.</li>
                      <li>Duşlarda 5 dakikadan fazla kalmayınız.</li>
                      <li>Musluk ve lambaları açık bırakmayınız.</li>
                      <li>Soyunma odalarında sigara içilmez, yiyecek, alkollü içecek ve sakız kullanılamaz.</li>
                      <li>Tesis ayrılırken kilitli bırakılan dolaplar gün sonunda açılır; içindeki eşyalar idare tarafından 15 gün muhafaza edilir, sonrasında hayır kurumuna bağışlanır. Unutulan terli ve ıslak eşyalar muhafaza edilmeden imha edilir.</li>
                      <li>Hijyen ve güvenlik gereği tüm eşyalar soyunma dolabına yerleştirilmeli, hiçbir eşya dolap dışında bırakılmamalıdır.</li>
                    </ul>
                  </>
                )}
              </div>
            </div>
            <div className="sa-terms-modal-footer">
              <button className="sa-submit-button" onClick={() => { setFormData({ ...formData, termsAccepted: true }); setShowTermsModal(false); }}>Okudum, Anladım</button>
              <button className="sa-btn-cancel" onClick={() => setShowTermsModal(false)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {showPoolGuardianConsentModal && (
        <div className="sa-terms-modal-overlay" onClick={() => setShowPoolGuardianConsentModal(false)}>
          <div className="sa-terms-modal" style={{ maxWidth: '650px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="sa-terms-modal-header">
              <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#1e293b', fontWeight: '700' }}>
                İSKENDERUN BELİYİESİ VELİ İZİN FORMU & SAĞLIK BEYANI
              </h3>
              <button className="sa-terms-modal-close" onClick={() => setShowPoolGuardianConsentModal(false)}>&times;</button>
            </div>

            <div className="sa-terms-modal-body" style={{ fontSize: '0.9rem', color: '#334155', lineHeight: '1.6' }}>
              <div style={{ backgroundColor: '#f8fafc', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '1rem' }}>
                <p style={{ margin: '0 0 6px 0', fontWeight: '600', color: '#0f172a' }}>Katılımcı Bilgileri</p>
                <p style={{ margin: '2px 0' }}><strong>Katılımcı:</strong> {formData.firstName} {formData.lastName} ({formData.identityNumber})</p>
                <p style={{ margin: '2px 0' }}><strong>Doğum Tarihi:</strong> {formData.birthDate}</p>
                <p style={{ margin: '2px 0' }}><strong>Başvuru Türü:</strong> Havuz Kart / Üyelik Başvurusu</p>
              </div>

              <p style={{ fontWeight: '600', marginBottom: '8px', color: '#0f172a' }}>Veli Bilgileri (6-18 Yaş Arası İçin Zorunlu)</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '1rem' }}>
                {/* Velinin Adı Soyadı */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px' }}>Velinin Adı Soyadı <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    type="text"
                    className={`sa-form-input${guardianErrors.guardianFullName ? ' sa-input-error' : ''}`}
                    placeholder="Velinin Adı Soyadı"
                    value={guardianConsentData.guardianFullName}
                    onChange={(e) => {
                      const val = e.target.value;
                      setGuardianConsentData({ ...guardianConsentData, guardianFullName: val });
                      if (val.trim().length >= 2) setGuardianErrors((prev) => ({ ...prev, guardianFullName: '' }));
                    }}
                  />
                  {guardianErrors.guardianFullName && (
                    <p style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: '4px', marginBottom: 0 }}>{guardianErrors.guardianFullName}</p>
                  )}
                </div>

                {/* Velinin TC Kimlik No */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px' }}>Velinin T.C. Kimlik No <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    type="text"
                    className={`sa-form-input${guardianErrors.guardianIdentityNumber ? ' sa-input-error' : ''}`}
                    maxLength="11"
                    placeholder="11 Haneli TC Kimlik No"
                    value={guardianConsentData.guardianIdentityNumber}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 11);
                      setGuardianConsentData({ ...guardianConsentData, guardianIdentityNumber: val });
                      if (val.length === 11 && val[0] !== '0') setGuardianErrors((prev) => ({ ...prev, guardianIdentityNumber: '' }));
                    }}
                  />
                  {guardianErrors.guardianIdentityNumber && (
                    <p style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: '4px', marginBottom: 0 }}>{guardianErrors.guardianIdentityNumber}</p>
                  )}
                </div>


                {/* Velinin Doğum Yeri */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px' }}>Velinin Doğum Yeri <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    type="text"
                    className={`sa-form-input${guardianErrors.guardianBirthPlace ? ' sa-input-error' : ''}`}
                    placeholder="Örn: İskenderun"
                    value={guardianConsentData.guardianBirthPlace}
                    onChange={(e) => {
                      const val = e.target.value;
                      setGuardianConsentData({ ...guardianConsentData, guardianBirthPlace: val });
                      if (val.trim().length >= 2) setGuardianErrors((prev) => ({ ...prev, guardianBirthPlace: '' }));
                    }}
                  />
                  {guardianErrors.guardianBirthPlace && (
                    <p style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: '4px', marginBottom: 0 }}>{guardianErrors.guardianBirthPlace}</p>
                  )}
                </div>

                {/* Velinin Doğum Tarihi */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px' }}>Velinin Doğum Tarihi <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    type="date"
                    className={`sa-form-input${guardianErrors.guardianBirthDate ? ' sa-input-error' : ''}`}
                    value={guardianConsentData.guardianBirthDate}
                    max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                    onChange={(e) => {
                      const val = e.target.value;
                      setGuardianConsentData({ ...guardianConsentData, guardianBirthDate: val });
                      if (val) setGuardianErrors((prev) => ({ ...prev, guardianBirthDate: '' }));
                    }}
                  />
                  {guardianErrors.guardianBirthDate && (
                    <p style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: '4px', marginBottom: 0 }}>{guardianErrors.guardianBirthDate}</p>
                  )}
                </div>
              </div>

              <div style={{ backgroundColor: '#fffbe6', border: '1px solid #ffe58f', padding: '12px', borderRadius: '8px', marginBottom: '1rem' }}>
                <p style={{ margin: 0, fontWeight: '700', color: '#873800', marginBottom: '4px' }}>SAĞLIK BEYANI VE İZİN METNİ</p>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#613400' }}>
                  Velisi bulunduğum yukarıda bilgileri yazılı çocuğumun yüzme spor dalında spor yapmaya ve havuz tesisini kullanmaya engel bir sağlık durumunun bulunmadığını beyan eder, oluşabilecek her türlü sağlık probleminde sorumluluğun tarafıma ait olduğunu kabul ve taahhüt ederek katılımına izin veriyorum.
                </p>
              </div>

              <div
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '0.5rem',
                  padding: '10px 12px', borderRadius: '8px',
                  border: guardianErrors.isAccepted ? '2px solid #ef4444' : '2px solid transparent',
                  background: guardianErrors.isAccepted ? '#fef2f2' : 'transparent',
                  transition: 'border 0.2s, background 0.2s'
                }}
              >
                <input
                  type="checkbox"
                  id="cardGuardianConsentCheck"
                  checked={guardianConsentData.isAccepted}
                  onChange={(e) => {
                    setGuardianConsentData({ ...guardianConsentData, isAccepted: e.target.checked });
                    if (e.target.checked) setGuardianErrors((prev) => ({ ...prev, isAccepted: '' }));
                  }}
                  style={{ marginTop: '3px', cursor: 'pointer', width: '18px', height: '18px', flexShrink: 0 }}
                />
                <label htmlFor="cardGuardianConsentCheck" style={{ cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600', color: '#0f172a' }}>
                  Veli İzin Formu ve Sağlık Beyanı metnini okudum, yukarıdaki bilgilerin doğruluğunu ve şartları kabul ediyorum.
                </label>
              </div>
              {guardianErrors.isAccepted && (
                <p style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: '2px', marginBottom: '0.5rem', paddingLeft: '4px' }}>{guardianErrors.isAccepted}</p>
              )}
            </div>

            <div className="sa-terms-modal-footer" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                className="sa-btn-cancel"
                onClick={() => {
                  setShowPoolGuardianConsentModal(false);
                  setGuardianErrors({});
                }}
              >
                Vazgeç
              </button>
              <button
                className="sa-submit-button"
                onClick={() => {
                  const errors = {};

                  // Velinin Adı Soyadı
                  if (!guardianConsentData.guardianFullName.trim() || guardianConsentData.guardianFullName.trim().split(' ').length < 2) {
                    errors.guardianFullName = 'Lütfen velinin ad ve soyadını giriniz (en az iki kelime).';
                  }

                  // Velinin TC Kimlik No
                  const tcDigits = guardianConsentData.guardianIdentityNumber.replace(/\D/g, '');
                  if (tcDigits.length !== 11) {
                    errors.guardianIdentityNumber = 'TC Kimlik No 11 haneli olmalıdır.';
                  } else if (tcDigits[0] === '0') {
                    errors.guardianIdentityNumber = 'TC Kimlik No 0 ile başlamaz.';
                  }

                  // Doğum Yeri
                  if (!guardianConsentData.guardianBirthPlace.trim() || guardianConsentData.guardianBirthPlace.trim().length < 2) {
                    errors.guardianBirthPlace = 'Lütfen velinin doğum yerini giriniz.';
                  }

                  // Doğum Tarihi
                  if (!guardianConsentData.guardianBirthDate) {
                    errors.guardianBirthDate = 'Lütfen velinin doğum tarihini seçiniz.';
                  } else {
                    const birthYear = new Date(guardianConsentData.guardianBirthDate).getFullYear();
                    const currentYear = new Date().getFullYear();
                    if (currentYear - birthYear < 18) {
                      errors.guardianBirthDate = 'Veli 18 yaşından büyük olmalıdır.';
                    }
                  }

                  // Onay kutusu
                  if (!guardianConsentData.isAccepted) {
                    errors.isAccepted = 'Formu onaylamak için lütfen onay kutusunu işaretleyiniz.';
                  }

                  if (Object.keys(errors).length > 0) {
                    setGuardianErrors(errors);
                    toast.error('Lütfen eksik veya hatalı alanları düzeltiniz.');
                    return;
                  }

                  setGuardianErrors({});
                  setShowPoolGuardianConsentModal(false);
                  handleContinueToPayment(true);
                }}
              >
                Onayla ve Devam Et
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const cropModalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.85)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 9999,
  padding: '20px'
};

const cropModalStyle = {
  position: 'relative',
  width: '100%',
  maxWidth: '600px',
  height: '70vh',
  backgroundColor: '#fff',
  borderRadius: '8px',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column'
};

const cropControlsStyle = {
  height: '60px',
  display: 'flex',
  alignItems: 'center',
  padding: '0 15px',
  backgroundColor: '#f8f9fa',
  borderTop: '1px solid #e9ecef',
  justifyContent: 'flex-end',
  gap: '10px'
};

const cropBtnCancel = {
  padding: '8px 16px',
  borderRadius: '4px',
  border: '1px solid #ccc',
  background: '#fff',
  color: '#333',
  cursor: 'pointer',
  fontWeight: '500'
};

const cropBtnSave = {
  padding: '8px 16px',
  borderRadius: '4px',
  border: 'none',
  background: '#1cc88a',
  color: '#fff',
  cursor: 'pointer',
  fontWeight: '600'
};

export default CardApplicationForm;

